import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AFRICAN_COUNTRIES } from "@/lib/countries";
import { Loader2 } from "lucide-react";

const CATEGORIES = [
    { v: "fashion", l: "Mode & Vêtements" },
    { v: "beauty", l: "Beauté & Cosmétique" },
    { v: "food", l: "Nourriture & Restauration" },
    { v: "electronics", l: "Électronique" },
    { v: "home", l: "Maison & Déco" },
    { v: "services", l: "Services" },
    { v: "cars", l: "Automobile" },
    { v: "other", l: "Autre" },
];

export default function EditProductModal({ product, open, onClose, onUpdated }) {
    const [form, setForm] = useState({
        title: "", description: "", price: "", category: "other", city: "", country: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (product) {
            setForm({
                title: product.title || "",
                description: product.description || "",
                price: String(product.price ?? ""),
                category: product.category || "other",
                city: product.city || "",
                country: product.country || "",
            });
        }
    }, [product]);

    const submit = async () => {
        if (!form.title || !form.description || !form.price || !form.city || !form.country) {
            toast.error("Remplissez tous les champs");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch(`/products/${product.id}`, {
                title: form.title,
                description: form.description,
                price: Number(form.price),
                category: form.category,
                city: form.city,
                country: form.country,
            });
            toast.success("Produit mis à jour");
            onUpdated?.(data);
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="bg-[#0f0f0f] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-product-modal">
                <DialogHeader>
                    <DialogTitle className="font-display">Modifier le produit</DialogTitle>
                    <DialogDescription className="text-white/60 text-xs font-body">
                        Mettez à jour les informations. La vidéo et la photo ne sont pas modifiables ici.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-white/80 font-body">Titre *</Label>
                        <Input
                            data-testid="edit-title"
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            className="mt-1 bg-white/5 border-white/10 h-11 text-white"
                        />
                    </div>
                    <div>
                        <Label className="text-white/80 font-body">Description *</Label>
                        <Textarea
                            data-testid="edit-description"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            rows={3}
                            className="mt-1 bg-white/5 border-white/10 text-white"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-white/80 font-body">Prix (FCFA) *</Label>
                            <Input
                                data-testid="edit-price"
                                type="number"
                                value={form.price}
                                onChange={(e) => setForm({ ...form, price: e.target.value })}
                                className="mt-1 bg-white/5 border-white/10 h-11 text-white"
                            />
                        </div>
                        <div>
                            <Label className="text-white/80 font-body">Catégorie</Label>
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger className="mt-1 bg-white/5 border-white/10 h-11 text-white" data-testid="edit-category">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map((c) => (
                                        <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-white/80 font-body">Ville *</Label>
                            <Input
                                data-testid="edit-city"
                                value={form.city}
                                onChange={(e) => setForm({ ...form, city: e.target.value })}
                                className="mt-1 bg-white/5 border-white/10 h-11 text-white"
                            />
                        </div>
                        <div>
                            <Label className="text-white/80 font-body">Pays *</Label>
                            <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                                <SelectTrigger className="mt-1 bg-white/5 border-white/10 h-11 text-white" data-testid="edit-country">
                                    <SelectValue placeholder="Choisir" />
                                </SelectTrigger>
                                <SelectContent className="max-h-64">
                                    {AFRICAN_COUNTRIES.map((c) => (
                                        <SelectItem key={c} value={c}>{c}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        onClick={submit}
                        disabled={saving}
                        data-testid="edit-submit"
                        className="orange-btn rounded-full h-11 w-full font-body font-bold"
                    >
                        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</> : "Enregistrer"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
